import { computed, ref, watch } from 'vue';
import { ProductService } from '@/services/ProductService';
import { useMainStore } from '@/store/main';
import { useNotification } from '@gohighlevel/ghl-ui';
import { useInfiniteQuery } from '@tanstack/vue-query';
import { debounce } from 'lodash';
export function useProductSearch({ enabled }) {
    const { altId: currentLocationId } = useMainStore();
    const notification = useNotification();
    const PAGE_LIMIT = 20;
    const currentPageNumber = ref(1);
    async function searchLogic(search, page = 1, signal) {
        const reqBody = {
            locationId: currentLocationId,
            limit: PAGE_LIMIT,
            offset: (page - 1) * PAGE_LIMIT,
            expand: ['tax'],
        };
        if (search) {
            reqBody.search = search;
        }
        return await ProductService.getProducts(reqBody);
    }
    const searchText = ref('');
    const { data, isFetching, fetchNextPage, isError, error } = useInfiniteQuery({
        queryKey: ['search-products', currentLocationId, searchText],
        queryFn: async ({ pageParam, signal }) => {
            currentPageNumber.value = pageParam;
            const res = await searchLogic(searchText.value, pageParam, signal);
            return res.data;
        },
        initialPageParam: 1,
        getNextPageParam: lastPage => {
            const nextPage = currentPageNumber.value + 1;
            const totalCount = lastPage?.total?.[0]?.total ?? 0;
            const totalPages = Math.ceil(totalCount / PAGE_LIMIT);
            if (nextPage > totalPages)
                return null;
            return nextPage;
        },
        staleTime: 15000,
        refetchOnWindowFocus: false,
        enabled,
    });
    const clearQuery = () => {
        searchText.value = '';
    };
    const search = debounce((text) => {
        searchText.value = text;
    }, 300);
    watch([isError], () => {
        if (error.value)
            return;
        notification.error({
            content: error?.value?.message ||
                'Something went wrong while fetching contacts.',
        });
    });
    const productsData = computed(() => (data.value?.pages || []).reduce((acc, page) => acc.concat(page.products), []));
    return {
        productsData,
        search,
        loading: isFetching,
        clearQuery,
        fetchNextPage,
    };
}
